import { useEffect, useState } from "react";
import WishlistButton from "./WishlistButton";
import { useCart } from "../context/CartContext";

const shuffleArray = (array) =>
  array
    .map((item) => ({ item, sort: Math.random() }))
    .sort((a, b) => a.sort - b.sort)
    .map(({ item }) => item);

export default function FeaturedCarousel({
  products = [],
  onClick,
  onView,
  }) {

  const [shuffledProducts, setShuffledProducts] = useState([]);
  const [showPopup] = useState(false);
  const {
    selectedFlowers,
    selectedVips,
    selectedAccessories,
    addFlower,
    removeFlower,
    addAccessory,
    removeAccessory,
    addVip,
    removeVip,
  } = useCart();

  useEffect(() => {
    setShuffledProducts(shuffleArray(products));
  }, [products]);

  useEffect(() => {
    const carousel = document.getElementById("featured-carousel");
    if (!carousel) return;

    const scrollStep = 300;
    const scrollDelay = 5000;

    const scroll = () => {
      const maxScrollLeft = carousel.scrollWidth - carousel.clientWidth;
      const isAtEnd = Math.ceil(carousel.scrollLeft + scrollStep) >= maxScrollLeft;

      if (isAtEnd) {
        carousel.scrollTo({ left: maxScrollLeft, behavior: "smooth" });
        setTimeout(() => {
          carousel.scrollTo({ left: 0, behavior: "smooth" });
        }, 2000);
      } else {
        carousel.scrollBy({ left: scrollStep, behavior: "smooth" });
      }
    };

    const interval = setInterval(scroll, scrollDelay);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    document.body.classList.toggle("no-scroll", showPopup);
    return () => document.body.classList.remove("no-scroll");
  }, [showPopup]);

  const handleAddClick = async (product, e) => {
    e?.stopPropagation?.();
    if (!product.inStock) return;

    const isFlower = product.category === "flower";
    const isAccessory = product.category === "accessory";
    const isVipProduct = product.category === "vip";

    const isSelected = isFlower
      ? selectedFlowers.some((f) => f._id === product._id)
      : isAccessory
      ? selectedAccessories.some((a) => a._id === product._id)
      : isVipProduct
      ? selectedVips.some((v) => v._id === product._id)
      : false;

    // ---------------- FLOWER ----------------
    if (isFlower) {
      if (isSelected) {
        removeFlower(product._id);
      } else {
        addFlower(product);
      }
      return;
    }

    // ---------------- ACCESSORY ----------------
    if (isAccessory) {
      if (isSelected) {
        removeAccessory(product._id);
      } else {
        addAccessory(product);
      }
      return;
    }

    // ---------------- VIP ----------------
    if (isVipProduct) {
      if (isSelected) {
        removeVip(product._id);
      } else {
        addVip(product);
      }
      return;
    }
  };

  const handleView = (product) => {
    onView?.(product);
  };

  const isSelected = (product) => {
    if (!product || !product._id) return false;

    if (product.category === "flower") {
      return selectedFlowers.some((f) => f._id === product._id);
    }

    if (product.category === "accessory") {
      return selectedAccessories.some((a) => a._id === product._id);
    }

    if (product.category === "vip") {
      return selectedVips.some((v) => v._id === product._id);
    }

    return false;
  };

  return (
    <>
      <section className="px-4 md:px-6 py-16 max-w-6xl mx-auto relative" data-aos="fade-up">
        <h2 className="text-3xl font-bold text-center mb-10">Featured Products</h2>

        <div className="relative">
          <div
            id="featured-carousel"
            className="flex overflow-x-auto space-x-6 scroll-smooth scroll-pl-6 snap-x snap-mandatory pb-4 no-scrollbar"
          >
            {shuffledProducts.map((product, i) => (
              <div
                key={product._id}
                className="relative flex-shrink-0 w-72 bg-white shadow rounded-lg snap-start"
                data-aos="zoom-in"
                data-aos-delay={i * 100}
              >
                {product.vip && (
                  <span className="absolute top-2 left-2 bg-yellow-300 text-yellow-900 px-2 py-0.5 rounded-full text-xs z-10">
                    VIP
                  </span>
                )}

                <WishlistButton product={product} />

                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full h-48 object-cover rounded-t-lg cursor-pointer"
                  onClick={() => handleView(product)}
                />

                <div className="p-4">
                  <h3
                    className="font-semibold text-lg leading-snug line-clamp-2 min-h-[3rem] cursor-pointer"
                    onClick={() => handleView(product)}
                  >
                    {product.name}
                  </h3>

                  <p className="text-sm text-gray-600 mb-2 line-clamp-2 min-h-[2.5rem]">
                    {product.description || "Stylish, thoughtful, and perfect for gift pairings."}
                  </p>

                  <div className="flex items-center justify-between">
                    <span className="text-blue-600 font-bold">
                      ${Number(product.price).toFixed(2)}
                    </span>

                    <button
                      disabled={!product.inStock}
                      onClick={(e) => handleAddClick(product, e)}
                      className={`px-3 py-1 rounded text-sm font-medium transition ${
                        !product.inStock
                          ? "bg-gray-400 cursor-not-allowed text-white"
                          : isSelected(product)
                          ? "bg-green-600 text-white"
                          : "bg-blue-600 text-white hover:bg-blue-700"
                      }`}
                    >
                      {!product.inStock
                        ? "Out of Stock"
                        : isSelected(product)
                        ? "✓ Added"
                        : "+ Add"}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Arrows */}
          <button
            onClick={() =>
              document.getElementById("featured-carousel")?.scrollBy({ left: -300, behavior: "smooth" })
            }
            className="hidden md:flex absolute left-[-1.5rem] top-1/2 transform -translate-y-1/2 bg-blue-600 text-white p-2 rounded-full shadow hover:bg-blue-700 z-10"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button
            onClick={() =>
              document.getElementById("featured-carousel")?.scrollBy({ left: 300, behavior: "smooth" })
            }
            className="hidden md:flex absolute right-[-1.5rem] top-1/2 transform -translate-y-1/2 bg-blue-600 text-white p-2 rounded-full shadow hover:bg-blue-700 z-10"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </section>
    </>
  );
}
